import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { map, Observable } from 'rxjs';

export interface IndicatorRequestBody {
	show_mine: boolean;
	filterDate: string;
	location: string;
	type: string | number;
	subType: string | number;
	getType: string;
	getterLimit: number;
	// dateRange: number; // Deprecated: usando period_type agora
	startDate?: string;
	endDate?: string;
}

export interface IndicatorValue {
	data: number[];
	labels: string[];
}

export interface Indicator {
	label: string;
	values: IndicatorValue[];
	id: number;
	indicator_id: string;
	type: string;
	name: string;
	family: string;
}

export interface IndicatorFilters {
	show_mine: boolean;
	custom_end_date: string;
	location: string;
	subType: string;
	getType: string;
	custom_start_date: string;
	filterDate: string;
	getterLimit: number;
	type: string;
}

export interface IndicatorDateRange {
	start: string;
	end: string;
}

export interface IndicatorResponse {
	status: string;
	indicators: Indicator[];
	filters: IndicatorFilters;
	dateRange: IndicatorDateRange;
}

export interface FilteredHeaderItem {
	inputType: string;
	rawValues: any[];
	label: string;
	values: any[];
	name: string;
	items?: {
		inputType: string;
		label: string;
		values: any[];
		name: string;
	}[];
}

export interface FilteredHeadersResponse {
	[key: string]: FilteredHeaderItem;
}

@Injectable({
	providedIn: 'root',
})
export class IndicatorsService {
	private indicatorsRealtimeURL = environment.baseApiUrl + 'cockpit';
	private getAvailableCiTypesUrl = environment.baseApiUrl + 'myasset/ciFilter';
	private getAllLocalitysURL = environment.baseApiUrl + 'location/search';
	private getAllManufacturersURL = environment.baseApiUrl + 'manufacturer/searchManufacturer';
	private getAllModelsURL = environment.baseApiUrl + 'manufacturer/searchModel';
	private getAllSubtypesURL = environment.baseApiUrl + 'manufacturer/searchSubtypes';
	private getFilteredHeadersURL = environment.baseApiUrl + 'cmdbhp/headers/filtered';

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService
	) {}

	getRealtimeIndicators(body: IndicatorRequestBody): Observable<IndicatorResponse> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		let parameters = new HttpParams();
		// .set('dateRange', body.dateRange.toString()); // Deprecated: usando period_type agora

		// Adiciona startDate e endDate apenas se estiverem definidos
		if (body.startDate) {
			parameters = parameters.set('startDate', body.startDate);
		}
		if (body.endDate) {
			parameters = parameters.set('endDate', body.endDate);
		}

		return this.http.get<IndicatorResponse>(this.indicatorsRealtimeURL, { headers: headers, params: parameters });
	}

	getAvailablesCiTypes(): Observable<any> {
		const getAvailableCiTypesUrl = this.getAvailableCiTypesUrl;
		return this.http.get<any>(getAvailableCiTypesUrl, { headers: this.headers });
	}

	getCockpitData(parameters: any): Observable<IndicatorResponse> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		let httpParameters = new HttpParams();

		// Adiciona todos os parâmetros à URL
		for (const key of Object.keys(parameters)) {
			if (parameters[key] !== undefined && parameters[key] !== null && parameters[key] !== '') {
				httpParameters = httpParameters.set(key, parameters[key].toString());
			}
		}

		return this.http.get<IndicatorResponse>(this.indicatorsRealtimeURL, {
			headers: headers,
			params: httpParameters,
		});
	}

	getAvailablesLocations(): Observable<any[]> {
		const defaultCustomer = this.ProfileService.userData().idCustomer;
		const getAllLocalitysURL = `${this.getAllLocalitysURL}?customer=${defaultCustomer}`;

		return this.http.get<any[]>(getAllLocalitysURL, { headers: this.headers }).pipe(
			map((localityRes) => {
				const newLocalityArray = [];
				for (const resp of localityRes) {
					const newLocality = {
						id_location: resp.id,
						location: resp.location,
						id_site: resp.site,
						site: resp.name_site,
					};
					newLocalityArray.push(newLocality);
				}
				return newLocalityArray;
			})
		);
	}

	getAvailablesManufacturers(): Observable<any[]> {
		return this.http.get<any[]>(this.getAllManufacturersURL, { headers: this.headers });
	}

	getAvailablesModels(): Observable<any[]> {
		return this.http.get<any[]>(this.getAllModelsURL, { headers: this.headers });
	}

	getAvailablesSubTypesByCiType(ciType: string): Observable<any[]> {
		const url = `${this.getAllSubtypesURL}?ci_type=${ciType}`;
		return this.http.get<any[]>(url, { headers: this.headers });
	}

	getAvailablesSubTypes(): Observable<any[]> {
		return this.http.get<any[]>(this.getAllSubtypesURL, { headers: this.headers });
	}

	getFilteredHeaders(filterParameters: any): Observable<FilteredHeaderItem[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		let httpParameters = new HttpParams();

		// Adiciona showActiveOnlyCis
		httpParameters = httpParameters.set('showActiveOnlyCis', 'true');

		// Adiciona parâmetros de filtro
		if (filterParameters.id !== undefined) {
			httpParameters = httpParameters.set('id', filterParameters.id);
		}
		if (filterParameters.type !== undefined && filterParameters.type !== '') {
			httpParameters = httpParameters.set('type', filterParameters.type);
		}
		if (filterParameters.identification !== undefined) {
			httpParameters = httpParameters.set('identification', filterParameters.identification);
		}
		if (filterParameters.status !== undefined && filterParameters.status !== '') {
			httpParameters = httpParameters.set('status', filterParameters.status);
		}
		if (filterParameters.location !== undefined && filterParameters.location !== '') {
			httpParameters = httpParameters.set('location', filterParameters.location);
		}
		if (filterParameters.impact !== undefined) {
			httpParameters = httpParameters.set('impact', filterParameters.impact);
		}
		if (filterParameters.sub_type !== undefined && filterParameters.sub_type !== '') {
			httpParameters = httpParameters.set('sub_type', filterParameters.sub_type);
		}
		if (filterParameters.manufacturer !== undefined) {
			httpParameters = httpParameters.set('manufacturer', filterParameters.manufacturer);
		}
		if (filterParameters.model !== undefined) {
			httpParameters = httpParameters.set('model', filterParameters.model);
		}
		if (filterParameters.digital_term_status !== undefined) {
			httpParameters = httpParameters.set('digital_term_status', filterParameters.digital_term_status);
		}

		// Adiciona general como vazio se não houver filtros específicos
		if (!httpParameters.has('general')) {
			httpParameters = httpParameters.set('general', '');
		}

		return this.http.get<FilteredHeaderItem[]>(this.getFilteredHeadersURL, {
			headers: headers,
			params: httpParameters,
		});
	}
}
